import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { SDeleteKoboProject } from "../../../../apis/app.service";
import { IKoboProject } from "../../core/models";
import "./ConfirmKoboDelete.scss";

interface ConfirmKoboDeleteProps {
  project: IKoboProject;
  onClose: () => void;
}

const ConfirmKoboDelete: React.FC<ConfirmKoboDeleteProps> = ({
  project,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => SDeleteKoboProject(projectId),
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate(project._id);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content confirm-delete"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="warning-icon">⚠️</div>
          <h2>Delete Kobo Project</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <p className="warning-message">
            Are you sure you want to delete this Kobo project?
          </p>
          <div className="project-info">
            <p>
              <strong>Project Name:</strong> {project.name}
            </p>
            <p>
              <strong>Asset UID:</strong> {project.asset_uid}
            </p>
            {project.institution && (
              <p>
                <strong>Institution:</strong> {project.institution.name}
              </p>
            )}
          </div>
          <div className="warning-note">
            <p>
              <strong>Note:</strong> This action cannot be undone. The project
              will be removed from SEMA, but your data in KoboToolbox will
              remain unchanged.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete Project"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmKoboDelete;
